<script setup lang="ts">
import {
  HANDOVER_FROM_YEAR,
  handoverTokens,
  parseHandoverTokens,
  type HandoverState,
} from '~/utils/explore/handoverFilter';
import { useDebounceFn } from '@vueuse/core';
import type { Filters } from '~/types/properties';
import { usePropertyAutocomplete } from '~/composables/usePropertyAutocomplete';
import { PROPERTIES_DICTIONARY_KEYS, usePCDictionary } from '~/api/queries/dictionaries';
import { CANONICAL_SALE_STATUS, toCanonicalSaleStatus } from '~/constants/properties';
import PropertiesFiltersPopup from '~/components/properties/FiltersPopup.vue';
import ExclusiveInfoModal from '~/components/explore/ExclusiveInfoModal.vue';
import { useExclusiveInfo } from '~/composables/useExclusiveInfo';
import { exclusiveChipClasses } from '~/utils/exclusives/exclusiveExploreState';

const { t } = useI18n();
const { currentLocale } = useLocaleSwitch();
const { formatCurrency } = useFormat();

const filters = defineModel<Filters>({ required: true });

const { resultCount = 0, isLoading = false } = defineProps<{
  resultCount?: number;
  isLoading?: boolean;
}>();

const emit = defineEmits<{
  (e: 'apply'): void;
  (e: 'reset'): void;
  (
    e: 'selectSuggestion',
    payload: { type: 'project' | 'developer' | 'area'; id: string; name: string; raw?: unknown },
  ): void;
}>();

// ── Smart search ────────────────────────────────────────────────
const searchAnchorRef = ref<HTMLElement | null>(null);
const isSearchFocused = ref(false);

const {
  grouped: smartSearchGrouped,
  isFallbackSuggestions: smartSearchIsFallback,
  isLoading: smartSearchLoading,
  searchProperties: fetchSmartSearchSuggestions,
} = usePropertyAutocomplete();

const showSmartSearchDropdown = computed(() => isSearchFocused.value && (filters.value.query?.trim().length ?? 0) >= 2);

const debouncedSmartSearch = useDebounceFn((query: string) => {
  if (query.trim().length >= 2) fetchSmartSearchSuggestions(query);
}, 250);

watch(
  () => filters.value.query,
  (q) => {
    if ((q?.trim().length ?? 0) >= 2) debouncedSmartSearch(q ?? '');
  },
);

const debouncedQueryApply = useDebounceFn(() => emit('apply'), 400);

const onQueryInput = (event: Event) => {
  const value = (event.target as HTMLInputElement).value;
  filters.value.query = value;
  debouncedQueryApply();
};

const onSmartSearchSelect = (payload: {
  type: 'project' | 'developer' | 'area';
  id: string;
  name: string;
  raw?: unknown;
}) => emit('selectSuggestion', payload);

const clearQuery = () => {
  filters.value.query = '';
  emit('apply');
};

// ── Dictionaries ────────────────────────────────────────────────
const { data: developersDict } = usePCDictionary(PROPERTIES_DICTIONARY_KEYS.DEVELOPER_NAMES);
const { data: salesStatuses } = usePCDictionary(PROPERTIES_DICTIONARY_KEYS.SALE_STATUSES);
const { data: locationsDict } = usePCDictionary(PROPERTIES_DICTIONARY_KEYS.LOCATIONS);

// Developer options (ordered alphabetically; full list, not just top 6).
const developerOptions = computed(() =>
  ((developersDict.value as Array<{ id: number; name: string }> | undefined) ?? [])
    .map((d) => ({ value: d.name, label: d.name }))
    .sort((a, b) => a.label.localeCompare(b.label)),
);

const developerInitial = (label: string) => (label?.[0] || '?').toUpperCase();
const developerColorClass = (label: string) => {
  const n = label.toLowerCase();
  if (n.includes('emaar')) return 'bg-developer-emaar';
  if (n.includes('damac')) return 'bg-developer-damac';
  if (n.includes('sobha')) return 'bg-developer-sobha';
  if (n.includes('meraas')) return 'bg-developer-meraas';
  if (n.includes('nakheel')) return 'bg-developer-nakheel';
  if (n.includes('aldar')) return 'bg-developer-aldar';
  return 'bg-black-60';
};

// Inline search inputs on the Developer + Area dropdowns. We keep the
// filter local to the dropdown (no debounce / server call needed — the
// dictionary is already loaded client-side). The reset-on-close watcher
// is registered later, after `openDropdown` is declared.
const developerQuery = ref('');
const areaQuery = ref('');

const filteredDeveloperOptions = computed(() => {
  const q = developerQuery.value.trim().toLowerCase();
  if (!q) return developerOptions.value;
  return developerOptions.value.filter((o) => o.label.toLowerCase().includes(q));
});

const filteredAreaOptions = computed(() => {
  const q = areaQuery.value.trim().toLowerCase();
  if (!q) return areaOptions.value;
  return areaOptions.value.filter((o) => o.label.toLowerCase().includes(q));
});

// Area options come from the locations dictionary.
const areaOptions = computed(() =>
  ((locationsDict.value as Array<{ id: number; name: string }> | undefined) ?? [])
    .map((l) => ({ value: l.id, label: l.name }))
    .sort((a, b) => a.label.localeCompare(b.label)),
);

// Canonical sale-status options.
type CanonicalLabel = (typeof CANONICAL_SALE_STATUS)[keyof typeof CANONICAL_SALE_STATUS];
const stageOptions = computed<{ value: CanonicalLabel; label: string }[]>(() => [
  { value: CANONICAL_SALE_STATUS.PRE_LAUNCH, label: t('explore.filterBar.stage.preLaunch') },
  { value: CANONICAL_SALE_STATUS.EOI_STAGE, label: t('explore.filterBar.stage.earlyAccess') },
  { value: CANONICAL_SALE_STATUS.ON_SALE, label: t('explore.filterBar.stage.live') },
  { value: CANONICAL_SALE_STATUS.SOLD_OUT, label: t('explore.filterBar.stage.soldOut') },
]);

// Post-cutover the sale-status dictionary returns canonical Postgres
// status_code strings as both `id` and `code`. We round-trip them straight
// into `filters.value.sale_status`; the FilterBar groups them into the 4
// canonical buckets via toCanonicalSaleStatus on `code`.
const idsForCanonical = (label: CanonicalLabel): string[] => {
  const list = (salesStatuses.value as Array<{ id: string; name?: string; code?: string }> | undefined) ?? [];
  return list.filter((s) => toCanonicalSaleStatus(s.code || s.name || String(s.id)) === label).map((s) => String(s.id));
};

const selectedStages = computed<CanonicalLabel[]>(() => {
  const ids = filters.value.sale_status ?? [];
  if (ids.length === 0) return [];
  const labels = new Set<CanonicalLabel>();
  for (const opt of stageOptions.value) {
    const optIds = idsForCanonical(opt.value);
    if (optIds.some((id) => ids.includes(id))) labels.add(opt.value);
  }
  return Array.from(labels);
});

const toggleStage = (label: CanonicalLabel) => {
  const next = selectedStages.value.includes(label)
    ? selectedStages.value.filter((s) => s !== label)
    : [...selectedStages.value, label];
  filters.value.sale_status = Array.from(new Set(next.flatMap((l) => idsForCanonical(l))));
  emit('apply');
};

// Bedrooms.
const bedroomOptions = computed<{ value: number; label: string }[]>(() => [
  { value: 0, label: t('common.priceTable.studio') },
  { value: 1, label: t('explore.filterBar.bedroomOptions.oneBed') },
  { value: 2, label: t('explore.filterBar.bedroomOptions.twoBed') },
  { value: 3, label: t('explore.filterBar.bedroomOptions.threeBed') },
  { value: 4, label: t('explore.filterBar.bedroomOptions.fourPlusBed') },
]);

const selectedBedrooms = computed({
  get: () => (filters.value.bedrooms ?? []) as number[],
  set: (next: number[]) => {
    filters.value.bedrooms = next;
    emit('apply');
  },
});

const toggleBedroom = (val: number) => {
  selectedBedrooms.value = selectedBedrooms.value.includes(val)
    ? selectedBedrooms.value.filter((v) => v !== val)
    : [...selectedBedrooms.value, val];
};

// Payment plan: before handover / on or after handover, plus the post-handover flag.
const PAYMENT_PLAN_SPLITS = ['20/80', '30/70', '40/60', '50/50', '60/40'] as const;

const selectedPaymentPlans = computed(() => (filters.value.payment_plan ?? []) as string[]);
const hasPaymentPlanFilter = computed(
  () => selectedPaymentPlans.value.length > 0 || Boolean(filters.value.post_handover_payment_plan),
);
const paymentPlanCount = computed(
  () => selectedPaymentPlans.value.length + (filters.value.post_handover_payment_plan ? 1 : 0),
);

const togglePaymentPlan = (split: string) => {
  filters.value.payment_plan = selectedPaymentPlans.value.includes(split)
    ? selectedPaymentPlans.value.filter((v) => v !== split)
    : [...selectedPaymentPlans.value, split];
  emit('apply');
};

const togglePostHandover = () => {
  filters.value.post_handover_payment_plan = !filters.value.post_handover_payment_plan;
  emit('apply');
};

// Handover: Ready now, years (the last one "and later") and, for picked years, quarters.
const HANDOVER_YEARS = [2026, 2027, 2028, 2029] as const;
const HANDOVER_FROM = HANDOVER_FROM_YEAR;
const QUARTERS = [1, 2, 3, 4] as const;

const handoverState = computed(() => parseHandoverTokens((filters.value.handover_year ?? []).map(String)));
const hasHandoverFilter = computed(() => (filters.value.handover_year ?? []).length > 0);
const handoverCount = computed(() => {
  const s = handoverState.value;
  return (s.ready ? 1 : 0) + s.years.length + (s.from ? 1 : 0);
});

const setHandover = (next: HandoverState) => {
  filters.value.handover_year = handoverTokens(next);
  emit('apply');
};
const toggleHandoverReady = () => setHandover({ ...handoverState.value, ready: !handoverState.value.ready });
const toggleHandoverFrom = () => setHandover({ ...handoverState.value, from: !handoverState.value.from });
const toggleHandoverYear = (year: number) => {
  const s = handoverState.value;
  const years = s.years.includes(year) ? s.years.filter((y) => y !== year) : [...s.years, year];
  setHandover({ ...s, years, quarters: years.length ? s.quarters : [] });
};
const toggleHandoverQuarter = (q: number) => {
  const s = handoverState.value;
  setHandover({ ...s, quarters: s.quarters.includes(q) ? s.quarters.filter((x) => x !== q) : [...s.quarters, q] });
};

// Developers.
const selectedDevelopers = computed({
  get: () => filters.value.developer_names ?? [],
  set: (next: string[]) => {
    filters.value.developer_names = next;
    emit('apply');
  },
});
const toggleDeveloper = (name: string) => {
  selectedDevelopers.value = selectedDevelopers.value.includes(name)
    ? selectedDevelopers.value.filter((v) => v !== name)
    : [...selectedDevelopers.value, name];
};

// Areas.
const selectedAreas = computed({
  get: () => filters.value.location_ids ?? [],
  set: (next: number[]) => {
    filters.value.location_ids = next;
    emit('apply');
  },
});
const toggleArea = (id: number) => {
  selectedAreas.value = selectedAreas.value.includes(id)
    ? selectedAreas.value.filter((v) => v !== id)
    : [...selectedAreas.value, id];
};

// ── Price (dual-handle slider) ──────────────────────────────────
// Two stacked `<input type="range">` elements share a single track; the
// fill between them is rendered as an absolute-positioned div. Min thumb
// can't pass max thumb and vice versa (clamped on input). A min of 0 or
// max of PRICE_MAX_CAP collapses to `undefined` so the URL/params stay
// clean when the slider is at its extremes (i.e. "no filter").
const PRICE_MAX_CAP = 50_000_000;
const PRICE_STEP = 250_000;

// English keeps the original bespoke "AED 5M"/"AED 250K" formatting exactly
// (Intl's compact-currency notation can't replicate the boundary rounding —
// e.g. it always shows a decimal for whole millions, or rounds 999_999 up to
// "1M" instead of "1000K"). Other locales get a localized compact currency
// via useFormat() so digits/grouping/currency-name follow their convention.
const fmtPriceShort = (raw: number): string => {
  if (!raw) return '';
  if (currentLocale.value === 'en') {
    if (raw >= 1_000_000) {
      const m = raw / 1_000_000;
      const formatted = m % 1 === 0 ? m.toFixed(0) : m.toFixed(1);
      return `AED ${formatted}M`;
    }
    return `AED ${Math.round(raw / 1000)}K`;
  }
  return formatCurrency(raw, 'AED', {
    notation: 'compact',
    maximumFractionDigits: raw >= 1_000_000 ? 1 : 0,
    minimumFractionDigits: 0,
  });
};

const priceCapLabel = computed(() =>
  currentLocale.value === 'en'
    ? 'AED 50M+'
    : `${formatCurrency(PRICE_MAX_CAP, 'AED', { notation: 'compact', maximumFractionDigits: 1, minimumFractionDigits: 0 })}+`,
);

const priceLabel = computed(() => {
  const min = Number(filters.value.price_min ?? 0) || 0;
  const max = Number(filters.value.price_max ?? 0) || 0;
  if (min && max) return `${fmtPriceShort(min)} – ${fmtPriceShort(max)}`;
  if (max) return `Up to ${fmtPriceShort(max)}`;
  if (min) return `${fmtPriceShort(min)}+`;
  return '';
});

const hasPriceFilter = computed(() => Boolean(filters.value.price_min || filters.value.price_max));

// Effective slider positions — treat `undefined` as the corresponding
// extreme so the slider thumbs land correctly when the filter is blank.
const priceMinSliderValue = computed(() => Number(filters.value.price_min ?? 0) || 0);
const priceMaxSliderValue = computed(() => Number(filters.value.price_max ?? PRICE_MAX_CAP) || PRICE_MAX_CAP);

const pricePctMin = computed(() => (priceMinSliderValue.value / PRICE_MAX_CAP) * 100);
const pricePctMax = computed(() => (priceMaxSliderValue.value / PRICE_MAX_CAP) * 100);

const priceMinDisplay = computed(() =>
  priceMinSliderValue.value ? fmtPriceShort(priceMinSliderValue.value) : t('common.actions.any'),
);
const priceMaxDisplay = computed(() =>
  priceMaxSliderValue.value < PRICE_MAX_CAP ? fmtPriceShort(priceMaxSliderValue.value) : priceCapLabel.value,
);

const onPriceMinSliderInput = (event: Event) => {
  const raw = Number((event.target as HTMLInputElement).value);
  // Clamp so the min thumb can't cross the max thumb (leave a step's worth of room).
  const clamped = Math.min(raw, priceMaxSliderValue.value - PRICE_STEP);
  filters.value.price_min = clamped > 0 ? clamped : undefined;
};

const onPriceMaxSliderInput = (event: Event) => {
  const raw = Number((event.target as HTMLInputElement).value);
  const clamped = Math.max(raw, priceMinSliderValue.value + PRICE_STEP);
  filters.value.price_max = clamped < PRICE_MAX_CAP ? clamped : undefined;
};

const onPriceCommit = () => emit('apply');

const clearPrice = () => {
  filters.value.price_min = undefined;
  filters.value.price_max = undefined;
  emit('apply');
};

// ── Toggle filters (Favorites / Developer Vault) ────
const toggleFavoritesOnly = () => {
  filters.value.favorites_only = !filters.value.favorites_only;
  emit('apply');
};
const toggleDeveloperVault = () => {
  filters.value.developer_vault_only = !filters.value.developer_vault_only;
  emit('apply');
};

const toggleAvailableOnly = () => {
  filters.value.available_only = !filters.value.available_only;
  emit('apply');
};

// ── 100% Commission chip (ID-118) ───────────────────────────────
// Signed-out visitors can't see exclusives, so the chip is hidden for them. explore.vue's
// `filters.exclusive` watcher re-runs the queries, so the chip doesn't emit 'apply' itself.
const authStore = useAuthStore();
const { sendEventToBackend } = useTracking();
const {
  isOpen: isExclusiveInfoOpen,
  open: openExclusiveInfo,
  openIfFirstTime: openExclusiveInfoIfFirstTime,
  close: closeExclusiveInfo,
} = useExclusiveInfo();
const showExclusiveChip = computed(() => !!authStore.token);
const exclusiveChip = computed(() => exclusiveChipClasses(!!filters.value.exclusive));

const toggleExclusive = () => {
  const on = !filters.value.exclusive;
  filters.value.exclusive = on;
  void sendEventToBackend('exclusive_filter_toggled', { on, source: 'chip' });
  if (on) openExclusiveInfoIfFirstTime();
};

// ── More filters modal (FiltersPopup) ───────────────────────────
const isMoreFiltersOpen = ref(false);

const openMoreFilters = () => {
  isMoreFiltersOpen.value = true;
};

const onMoreFiltersInput = (next: Filters) => {
  filters.value = { ...filters.value, ...next };
  emit('apply');
};

const onMoreFiltersReset = () => {
  emit('reset');
};

// Count of advanced fields that are active (everything not already
// surfaced as a top-bar dropdown or toggle). Drives the badge on
// the "More filters" button. `location_ids`, `developer_names`,
// `bedrooms`, `sale_status`, price, payment plan already have their own
// dedicated UI on the top bar — exclude them here to avoid double
// counting.
const moreFiltersCount = computed(() => {
  const f = filters.value;
  let n = 0;
  if (f.apartment_type?.length) n++;
  if (f.amenities?.length) n++;
  if (f.emirate_names?.length) n++;
  if (f.building_status !== undefined && f.building_status !== null) n++;
  if (f.area_min) n++;
  if (f.area_max) n++;
  if (f.down_payment) n++;
  if (f.floor_plan) n++;
  if (f.floor_min) n++;
  if (f.floor_max) n++;
  return n;
});

// ── Dropdown open/close state machine ────────────────────────────
type DropdownKey = 'developer' | 'area' | 'price' | 'plan' | 'handover' | 'beds' | 'stage';
const openDropdown = ref<DropdownKey | null>(null);

// Reset the dropdown search input when the dropdown closes so reopening
// it doesn't surprise the user with a stale filter.
watch(
  () => openDropdown.value,
  (next, prev) => {
    if (prev === 'developer' && next !== 'developer') developerQuery.value = '';
    if (prev === 'area' && next !== 'area') areaQuery.value = '';
  },
);
const dropdownRefs = ref<Record<string, HTMLElement | null>>({});

const setDropdownRef = (key: DropdownKey) => (el: any) => {
  dropdownRefs.value[key] = el as HTMLElement | null;
};

const toggleDropdown = (key: DropdownKey) => {
  openDropdown.value = openDropdown.value === key ? null : key;
};

onMounted(() => {
  if (typeof document === 'undefined') return;
  const handler = (e: MouseEvent) => {
    if (!openDropdown.value) return;
    const el = dropdownRefs.value[openDropdown.value];
    if (el && !el.contains(e.target as Node)) openDropdown.value = null;
  };
  document.addEventListener('mousedown', handler);
  onBeforeUnmount(() => document.removeEventListener('mousedown', handler));
});

// ── Active filter count + clear ─────────────────────────────────
// Top-bar dropdown filters + toggles + the "More filters" badge so
// the agent sees a single accurate "N active" tally above the list.
const activeFilterCount = computed(() => {
  const f = filters.value;
  return (
    (f.developer_names?.length ? 1 : 0) +
    (f.location_ids?.length ? 1 : 0) +
    (f.sale_status?.length ? 1 : 0) +
    (f.bedrooms?.length ? 1 : 0) +
    (f.price_min || f.price_max ? 1 : 0) +
    (hasPaymentPlanFilter.value ? 1 : 0) +
    (hasHandoverFilter.value ? 1 : 0) +
    (f.exclusive ? 1 : 0) +
    (f.favorites_only ? 1 : 0) +
    (f.developer_vault_only ? 1 : 0) +
    (f.available_only ? 1 : 0) +
    moreFiltersCount.value
  );
});

const hasAnyActiveFilter = computed(() => activeFilterCount.value > 0 || !!filters.value.query);

// ── Dropdown button class helper (matches .pr-dd-btn states) ────
const ddBtnClass = (key: DropdownKey, hasValue: boolean) => {
  const isOpen = openDropdown.value === key;
  if (isOpen) return 'bg-black-100 text-white border-black-100';
  if (hasValue) return 'bg-white text-black-100 border-black-100';
  return 'bg-white text-black-60 border-black/[0.08] hover:bg-black-03';
};

const ddCntClass = (key: DropdownKey) => {
  const isOpen = openDropdown.value === key;
  return isOpen ? 'bg-white text-black-100' : 'bg-gold-primary-100 text-black-100';
};
</script>

<template>
  <div
    class="z-30 border-b border-black/[0.08] bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 tablet:sticky tablet:top-0"
  >
    <div class="mx-auto max-w-[1400px] px-4 py-3 tablet:px-8 tablet:py-3 desktop:px-10">
      <div class="flex flex-col gap-2">
        <!-- Row 1: search + summary (active count, Clear all, match count) on one baseline -->
        <div class="flex flex-wrap items-center gap-x-4 gap-y-2 tablet:flex-nowrap">
          <!-- Search (.pr-filter-search) -->
          <div
            ref="searchAnchorRef"
            class="relative flex h-10 w-full min-w-0 items-center gap-2 rounded-[10px] border border-black/[0.08] bg-white px-3 tablet:w-auto tablet:min-w-[280px] tablet:flex-1"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              class="text-black-60"
              aria-hidden="true"
            >
              <circle
                cx="11"
                cy="11"
                r="8"
              />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              :value="filters.query"
              type="text"
              class="flex-1 border-0 bg-transparent text-[14px] text-black-100 placeholder:text-black/40 focus:outline-none"
              :placeholder="t('explore.filterBar.searchPlaceholder')"
              @input="onQueryInput"
              @focus="isSearchFocused = true"
              @blur="isSearchFocused = false"
            />
            <button
              v-if="filters.query"
              type="button"
              class="text-black-40 hover:text-black-100"
              :aria-label="t('explore.filterBar.clearSearchAriaLabel')"
              @click="clearQuery"
            >
              <UIIcon
                name="big-close"
                class="!size-4"
              />
            </button>
            <PropertiesSmartSearchDropdown
              :visible="showSmartSearchDropdown"
              :grouped="smartSearchGrouped"
              :loading="smartSearchLoading"
              :is-fallback-suggestions="smartSearchIsFallback"
              :anchor-el="searchAnchorRef"
              dropdown-class="desktop:min-w-[420px]"
              @select="onSmartSearchSelect"
            />
          </div>

          <div
            v-if="hasAnyActiveFilter || resultCount > 0 || isLoading"
            class="flex shrink-0 items-center gap-3 whitespace-nowrap tablet:ms-auto"
            aria-live="polite"
          >
            <template v-if="hasAnyActiveFilter">
              <span class="font-mono text-[11px] uppercase tracking-[0.06em] text-black-60">
                {{ t('explore.filterBar.activeCount', { count: activeFilterCount }, activeFilterCount) }}
              </span>
              <button
                type="button"
                class="text-[12px] font-semibold text-black-60 hover:text-black-100"
                @click="emit('reset')"
              >
                {{ t('explore.filterBar.clearAll') }}
              </button>
            </template>
            <span
              v-if="hasAnyActiveFilter && (resultCount > 0 || isLoading)"
              class="h-3 w-px bg-black/[0.12]"
              aria-hidden="true"
            />
            <span
              v-if="resultCount > 0 || isLoading"
              class="font-mono text-[11px] uppercase tracking-[0.06em] text-black-60"
            >
              {{ t('explore.filterBar.projectsMatch', { count: resultCount }, resultCount) }}
            </span>
          </div>
        </div>

        <!-- Row 2: one chip group, equal heights; 100% Commission first, More filters last -->
        <div class="flex flex-wrap items-center gap-2">
          <!-- 100% Commission chip with its "i" attached (one gold segmented control) -->
          <div
            v-if="showExclusiveChip"
            class="inline-flex h-10 items-stretch"
          >
            <button
              type="button"
              class="inline-flex items-center gap-1.5 rounded-s-[10px] border px-3 text-[13px] font-semibold transition-colors"
              :class="exclusiveChip.chip"
              :aria-pressed="!!filters.exclusive"
              @click="toggleExclusive"
            >
              <UIIcon
                name="lighting"
                class="!size-3.5"
                :class="exclusiveChip.icon"
              />
              <span>{{ t('exclusives.chip.label') }}</span>
            </button>
            <button
              type="button"
              class="inline-flex w-9 items-center justify-center rounded-e-[10px] border border-s-0 transition-colors"
              :class="exclusiveChip.info"
              :aria-label="t('exclusives.chip.infoAriaLabel')"
              @click="openExclusiveInfo('info_icon')"
            >
              <UIIcon
                name="info-circle"
                class="!size-4"
              />
            </button>
          </div>

          <!-- Developer dropdown -->
          <div
            :ref="setDropdownRef('developer')"
            class="relative"
          >
            <button
              type="button"
              class="inline-flex h-10 items-center gap-2 rounded-[10px] border px-3 text-[13px] font-semibold transition-colors"
              :class="ddBtnClass('developer', selectedDevelopers.length > 0)"
              @click="toggleDropdown('developer')"
            >
              <span>{{ t('explore.filterBar.developerLabel') }}</span>
              <span
                v-if="selectedDevelopers.length"
                class="inline-flex h-5 min-w-5 items-center justify-center rounded-md px-1.5 font-mono text-[10px] font-semibold"
                :class="ddCntClass('developer')"
              >
                {{ selectedDevelopers.length }}
              </span>
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                aria-hidden="true"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            <div
              v-if="openDropdown === 'developer'"
              class="absolute start-0 top-[calc(100%+8px)] z-40 w-[240px] rounded-[10px] border border-black/[0.08] bg-white shadow-[0_8px_24px_rgba(13,15,21,0.06)]"
            >
              <div class="sticky top-0 border-b border-black/[0.06] bg-white p-2">
                <input
                  v-model="developerQuery"
                  type="search"
                  :placeholder="t('explore.filterBar.searchDeveloperPlaceholder')"
                  class="h-8 w-full rounded-md border border-black/[0.1] bg-white px-2 text-[12px] text-black-100 outline-none focus:border-black/[0.3]"
                />
              </div>
              <div class="max-h-[280px] overflow-y-auto p-2">
                <label
                  v-for="opt in filteredDeveloperOptions"
                  :key="opt.value"
                  class="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-black-100 hover:bg-black/[0.03]"
                >
                  <input
                    type="checkbox"
                    class="size-4 cursor-pointer accent-gold-primary-100"
                    :checked="selectedDevelopers.includes(opt.value)"
                    @change="toggleDeveloper(opt.value)"
                  />
                  <span
                    class="inline-flex size-[18px] items-center justify-center rounded-full text-[10px] font-bold text-white"
                    :class="developerColorClass(opt.label)"
                  >
                    {{ developerInitial(opt.label) }}
                  </span>
                  <span>{{ opt.label }}</span>
                </label>
                <div
                  v-if="!filteredDeveloperOptions.length"
                  class="px-2 py-3 text-center text-[12px] text-black-50"
                >
                  {{ t('explore.filterBar.noMatches') }}
                </div>
              </div>
            </div>
          </div>

          <!-- Area dropdown -->
          <div
            :ref="setDropdownRef('area')"
            class="relative"
          >
            <button
              type="button"
              class="inline-flex h-10 items-center gap-2 rounded-[10px] border px-3 text-[13px] font-semibold transition-colors"
              :class="ddBtnClass('area', selectedAreas.length > 0)"
              @click="toggleDropdown('area')"
            >
              <span>{{ t('explore.filterBar.areaLabel') }}</span>
              <span
                v-if="selectedAreas.length"
                class="inline-flex h-5 min-w-5 items-center justify-center rounded-md px-1.5 font-mono text-[10px] font-semibold"
                :class="ddCntClass('area')"
              >
                {{ selectedAreas.length }}
              </span>
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                aria-hidden="true"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            <div
              v-if="openDropdown === 'area'"
              class="absolute start-0 top-[calc(100%+8px)] z-40 w-[260px] rounded-[10px] border border-black/[0.08] bg-white shadow-[0_8px_24px_rgba(13,15,21,0.06)]"
            >
              <div class="sticky top-0 border-b border-black/[0.06] bg-white p-2">
                <input
                  v-model="areaQuery"
                  type="search"
                  :placeholder="t('explore.filterBar.searchAreaPlaceholder')"
                  class="h-8 w-full rounded-md border border-black/[0.1] bg-white px-2 text-[12px] text-black-100 outline-none focus:border-black/[0.3]"
                />
              </div>
              <div class="max-h-[280px] overflow-y-auto p-2">
                <label
                  v-for="opt in filteredAreaOptions"
                  :key="opt.value"
                  class="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-black-100 hover:bg-black/[0.03]"
                >
                  <input
                    type="checkbox"
                    class="size-4 cursor-pointer accent-gold-primary-100"
                    :checked="selectedAreas.includes(opt.value)"
                    @change="toggleArea(opt.value)"
                  />
                  <span>{{ opt.label }}</span>
                </label>
                <div
                  v-if="!filteredAreaOptions.length"
                  class="px-2 py-3 text-center text-[12px] text-black-50"
                >
                  {{ t('explore.filterBar.noMatches') }}
                </div>
              </div>
            </div>
          </div>

          <!-- Price (max-budget) dropdown -->
          <div
            :ref="setDropdownRef('price')"
            class="relative"
          >
            <button
              type="button"
              class="inline-flex h-10 items-center gap-2 rounded-[10px] border px-3 text-[13px] font-semibold transition-colors"
              :class="ddBtnClass('price', hasPriceFilter)"
              @click="toggleDropdown('price')"
            >
              <span>{{ t('explore.filterBar.priceLabel') }}</span>
              <span
                v-if="hasPriceFilter"
                class="inline-flex h-5 items-center rounded-md px-1.5 font-mono text-[10px] font-semibold"
                :class="ddCntClass('price')"
              >
                {{ priceLabel }}
              </span>
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                aria-hidden="true"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            <div
              v-if="openDropdown === 'price'"
              class="absolute start-0 top-[calc(100%+8px)] z-40 w-[300px] rounded-[10px] border border-black/[0.08] bg-white p-3 shadow-[0_8px_24px_rgba(13,15,21,0.06)]"
            >
              <div class="flex items-baseline justify-between text-[12px] text-black-60">
                <span class="font-mono uppercase tracking-[0.06em]">{{ t('explore.filterBar.priceRangeLabel') }}</span>
                <span class="text-[13px] font-semibold text-black-100">
                  {{ priceMinDisplay }} – {{ priceMaxDisplay }}
                </span>
              </div>
              <!-- Dual-handle range slider. Two overlaid native range inputs;
                   pointer-events disabled on the input track but re-enabled on
                   the thumbs (via scoped CSS below) so each thumb is grabbable
                   even though the inputs visually overlap. -->
              <div class="price-slider relative mt-5 h-5">
                <!-- Track -->
                <div class="absolute start-0 end-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-black/[0.08]" />
                <!-- Fill between thumbs -->
                <div
                  class="absolute top-1/2 h-1 -translate-y-1/2 rounded-full bg-black-100"
                  :style="{ insetInlineStart: pricePctMin + '%', insetInlineEnd: 100 - pricePctMax + '%' }"
                />
                <input
                  :value="priceMinSliderValue"
                  type="range"
                  min="0"
                  :max="PRICE_MAX_CAP"
                  :step="PRICE_STEP"
                  class="price-slider__input absolute inset-0 w-full"
                  :aria-label="t('explore.filterBar.minPriceAriaLabel')"
                  @input="onPriceMinSliderInput"
                  @change="onPriceCommit"
                />
                <input
                  :value="priceMaxSliderValue"
                  type="range"
                  min="0"
                  :max="PRICE_MAX_CAP"
                  :step="PRICE_STEP"
                  class="price-slider__input absolute inset-0 w-full"
                  :aria-label="t('explore.filterBar.maxPriceAriaLabel')"
                  @input="onPriceMaxSliderInput"
                  @change="onPriceCommit"
                />
              </div>
              <div
                class="mt-1 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.06em] text-black-40"
              >
                <span>{{ t('common.actions.any') }}</span>
                <span>{{ priceCapLabel }}</span>
              </div>
              <button
                v-if="hasPriceFilter"
                type="button"
                class="mt-3 inline-flex h-8 w-full items-center justify-center rounded-md border border-black/[0.08] bg-white text-[12px] font-semibold text-black-60 hover:bg-black-03 hover:text-black-100"
                @click="clearPrice"
              >
                {{ t('common.actions.clear') }}
              </button>
            </div>
          </div>

          <!-- Payment plan dropdown -->
          <div
            :ref="setDropdownRef('plan')"
            class="relative"
          >
            <button
              type="button"
              class="inline-flex h-10 items-center gap-2 rounded-[10px] border px-3 text-[13px] font-semibold transition-colors"
              :class="ddBtnClass('plan', hasPaymentPlanFilter)"
              data-testid="filter-payment-plan"
              @click="toggleDropdown('plan')"
            >
              <span>{{ t('explore.filterBar.paymentPlanLabel') }}</span>
              <span
                v-if="hasPaymentPlanFilter"
                class="inline-flex h-5 min-w-5 items-center justify-center rounded-md px-1.5 font-mono text-[10px] font-semibold"
                :class="ddCntClass('plan')"
              >
                {{ paymentPlanCount }}
              </span>
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                aria-hidden="true"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            <div
              v-if="openDropdown === 'plan'"
              class="absolute start-0 top-[calc(100%+8px)] z-40 w-[240px] rounded-[10px] border border-black/[0.08] bg-white p-3 shadow-[0_8px_24px_rgba(13,15,21,0.06)]"
            >
              <p class="mb-2 text-[11px] leading-snug text-black-60">{{ t('explore.filterBar.paymentPlanHint') }}</p>
              <div class="flex flex-wrap gap-1.5">
                <button
                  v-for="split in PAYMENT_PLAN_SPLITS"
                  :key="split"
                  type="button"
                  dir="ltr"
                  class="h-8 rounded-md border px-2.5 font-mono text-[12px] font-semibold transition-colors"
                  :class="
                    selectedPaymentPlans.includes(split)
                      ? 'border-gold-primary-100 bg-gold-primary-100 text-black-100'
                      : 'border-black/[0.08] bg-white text-black-100 hover:bg-black/[0.03]'
                  "
                  :aria-pressed="selectedPaymentPlans.includes(split)"
                  @click="togglePaymentPlan(split)"
                >
                  {{ split }}
                </button>
                <button
                  type="button"
                  class="h-8 rounded-md border px-2.5 text-[12px] font-semibold transition-colors"
                  :class="
                    filters.post_handover_payment_plan
                      ? 'border-gold-primary-100 bg-gold-primary-100 text-black-100'
                      : 'border-black/[0.08] bg-white text-black-100 hover:bg-black/[0.03]'
                  "
                  :aria-pressed="Boolean(filters.post_handover_payment_plan)"
                  @click="togglePostHandover"
                >
                  {{ t('explore.filterBar.postHandover') }}
                </button>
              </div>
            </div>
          </div>

          <!-- Handover dropdown -->
          <div
            :ref="setDropdownRef('handover')"
            class="relative"
          >
            <button
              type="button"
              class="inline-flex h-10 items-center gap-2 rounded-[10px] border px-3 text-[13px] font-semibold transition-colors"
              :class="ddBtnClass('handover', hasHandoverFilter)"
              data-testid="filter-handover"
              @click="toggleDropdown('handover')"
            >
              <span>{{ t('explore.filterBar.handoverLabel') }}</span>
              <span
                v-if="hasHandoverFilter"
                class="inline-flex h-5 min-w-5 items-center justify-center rounded-md px-1.5 font-mono text-[10px] font-semibold"
                :class="ddCntClass('handover')"
              >
                {{ handoverCount }}
              </span>
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                aria-hidden="true"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            <div
              v-if="openDropdown === 'handover'"
              class="absolute start-0 top-[calc(100%+8px)] z-40 w-[260px] rounded-[10px] border border-black/[0.08] bg-white p-3 shadow-[0_8px_24px_rgba(13,15,21,0.06)]"
            >
              <div class="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  class="h-8 rounded-md border px-2.5 text-[12px] font-semibold transition-colors"
                  :class="
                    handoverState.ready
                      ? 'border-gold-primary-100 bg-gold-primary-100 text-black-100'
                      : 'border-black/[0.08] bg-white text-black-100 hover:bg-black/[0.03]'
                  "
                  :aria-pressed="handoverState.ready"
                  @click="toggleHandoverReady"
                >
                  {{ t('explore.filterBar.handoverReadyNow') }}
                </button>
                <button
                  v-for="year in HANDOVER_YEARS"
                  :key="year"
                  type="button"
                  dir="ltr"
                  class="h-8 rounded-md border px-2.5 font-mono text-[12px] font-semibold transition-colors"
                  :class="
                    handoverState.years.includes(year)
                      ? 'border-gold-primary-100 bg-gold-primary-100 text-black-100'
                      : 'border-black/[0.08] bg-white text-black-100 hover:bg-black/[0.03]'
                  "
                  :aria-pressed="handoverState.years.includes(year)"
                  @click="toggleHandoverYear(year)"
                >
                  {{ year }}
                </button>
                <button
                  type="button"
                  dir="ltr"
                  class="h-8 rounded-md border px-2.5 font-mono text-[12px] font-semibold transition-colors"
                  :class="
                    handoverState.from
                      ? 'border-gold-primary-100 bg-gold-primary-100 text-black-100'
                      : 'border-black/[0.08] bg-white text-black-100 hover:bg-black/[0.03]'
                  "
                  :aria-pressed="handoverState.from"
                  @click="toggleHandoverFrom"
                >
                  {{ HANDOVER_FROM }}+
                </button>
              </div>
              <template v-if="handoverState.years.length">
                <p class="mb-1.5 mt-3 text-[11px] leading-snug text-black-60">
                  {{ t('explore.filterBar.handoverQuarterHint') }}
                </p>
                <div class="flex flex-wrap gap-1.5">
                  <button
                    v-for="q in QUARTERS"
                    :key="q"
                    type="button"
                    class="h-8 rounded-md border px-2.5 font-mono text-[12px] font-semibold transition-colors"
                    :class="
                      handoverState.quarters.includes(q)
                        ? 'border-gold-primary-100 bg-gold-primary-100 text-black-100'
                        : 'border-black/[0.08] bg-white text-black-100 hover:bg-black/[0.03]'
                    "
                    :aria-pressed="handoverState.quarters.includes(q)"
                    @click="toggleHandoverQuarter(q)"
                  >
                    {{ t('explore.filterBar.handoverQuarter', { quarter: q }) }}
                  </button>
                </div>
              </template>
            </div>
          </div>

          <!-- Beds dropdown -->
          <div
            :ref="setDropdownRef('beds')"
            class="relative"
          >
            <button
              type="button"
              class="inline-flex h-10 items-center gap-2 rounded-[10px] border px-3 text-[13px] font-semibold transition-colors"
              :class="ddBtnClass('beds', selectedBedrooms.length > 0)"
              @click="toggleDropdown('beds')"
            >
              <span>{{ t('explore.filterBar.bedsLabel') }}</span>
              <span
                v-if="selectedBedrooms.length"
                class="inline-flex h-5 min-w-5 items-center justify-center rounded-md px-1.5 font-mono text-[10px] font-semibold"
                :class="ddCntClass('beds')"
              >
                {{ selectedBedrooms.length }}
              </span>
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                aria-hidden="true"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            <div
              v-if="openDropdown === 'beds'"
              class="absolute start-0 top-[calc(100%+8px)] z-40 w-[200px] rounded-[10px] border border-black/[0.08] bg-white p-2 shadow-[0_8px_24px_rgba(13,15,21,0.06)]"
            >
              <label
                v-for="opt in bedroomOptions"
                :key="opt.value"
                class="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-black-100 hover:bg-black/[0.03]"
              >
                <input
                  type="checkbox"
                  class="size-4 cursor-pointer accent-gold-primary-100"
                  :checked="selectedBedrooms.includes(opt.value)"
                  @change="toggleBedroom(opt.value)"
                />
                <span>{{ opt.label }}</span>
              </label>
            </div>
          </div>

          <!-- Stage dropdown (canonical sale status) -->
          <div
            :ref="setDropdownRef('stage')"
            class="relative"
          >
            <button
              type="button"
              class="inline-flex h-10 items-center gap-2 rounded-[10px] border px-3 text-[13px] font-semibold transition-colors"
              :class="ddBtnClass('stage', selectedStages.length > 0)"
              @click="toggleDropdown('stage')"
            >
              <span>{{ t('explore.filterBar.stageLabel') }}</span>
              <span
                v-if="selectedStages.length"
                class="inline-flex h-5 min-w-5 items-center justify-center rounded-md px-1.5 font-mono text-[10px] font-semibold"
                :class="ddCntClass('stage')"
              >
                {{ selectedStages.length }}
              </span>
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                aria-hidden="true"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            <div
              v-if="openDropdown === 'stage'"
              class="absolute start-0 top-[calc(100%+8px)] z-40 w-[200px] rounded-[10px] border border-black/[0.08] bg-white p-2 shadow-[0_8px_24px_rgba(13,15,21,0.06)]"
            >
              <label
                v-for="opt in stageOptions"
                :key="opt.value"
                class="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-black-100 hover:bg-black/[0.03]"
              >
                <input
                  type="checkbox"
                  class="size-4 cursor-pointer accent-gold-primary-100"
                  :checked="selectedStages.includes(opt.value)"
                  @change="toggleStage(opt.value)"
                />
                <span>{{ opt.label }}</span>
              </label>
            </div>
          </div>

          <!-- Favorites only toggle -->
          <button
            type="button"
            class="inline-flex h-10 items-center gap-1.5 rounded-[10px] border px-3 text-[13px] font-semibold transition-colors"
            :class="
              filters.favorites_only
                ? 'border-black-100 bg-gold-primary-100 text-black-100 hover:bg-gold-primary-120'
                : 'border-black/[0.08] bg-white text-black-60 hover:bg-black-03'
            "
            :aria-pressed="!!filters.favorites_only"
            @click="toggleFavoritesOnly"
          >
            <UIIcon
              name="heart-filled"
              class="!size-3.5"
            />
            <span>{{ t('explore.filterBar.favoritesOnly') }}</span>
          </button>

          <!-- Developer Vault toggle -->
          <button
            type="button"
            class="inline-flex h-10 items-center gap-1.5 rounded-[10px] border px-3 text-[13px] font-semibold transition-colors"
            :class="
              filters.developer_vault_only
                ? 'border-black-100 bg-gold-primary-100 text-black-100 hover:bg-gold-primary-120'
                : 'border-black/[0.08] bg-white text-black-60 hover:bg-black-03'
            "
            :aria-pressed="!!filters.developer_vault_only"
            @click="toggleDeveloperVault"
          >
            <UIIcon
              name="training"
              class="!size-3.5"
            />
            <span>{{ t('explore.filterBar.developerVault') }}</span>
          </button>

          <!-- Available units only toggle — projects with live sellable
               inventory (offplan_listings sale rows). -->
          <button
            type="button"
            class="inline-flex h-10 items-center gap-1.5 rounded-[10px] border px-3 text-[13px] font-semibold transition-colors"
            :class="
              filters.available_only
                ? 'border-black-100 bg-gold-primary-100 text-black-100 hover:bg-gold-primary-120'
                : 'border-black/[0.08] bg-white text-black-60 hover:bg-black-03'
            "
            :aria-pressed="!!filters.available_only"
            @click="toggleAvailableOnly"
          >
            <UIIcon
              name="check"
              class="!size-3.5"
            />
            <span>{{ t('explore.filterBar.availableUnits') }}</span>
          </button>

          <!-- More filters (opens FiltersPopup modal) -->
          <button
            type="button"
            class="inline-flex h-10 items-center gap-1.5 rounded-[10px] border px-3 text-[13px] font-semibold transition-colors"
            :class="
              moreFiltersCount > 0
                ? 'border-black-100 bg-white text-black-100 hover:bg-black-03'
                : 'border-black/[0.08] bg-white text-black-60 hover:bg-black-03'
            "
            @click="openMoreFilters"
          >
            <UIIcon
              name="big-plus"
              class="!size-3.5"
            />
            <span>{{ t('explore.filterBar.moreFilters') }}</span>
            <span
              v-if="moreFiltersCount > 0"
              class="inline-flex h-5 min-w-5 items-center justify-center rounded-md bg-gold-primary-100 px-1.5 font-mono text-[10px] font-semibold text-black-100"
            >
              {{ moreFiltersCount }}
            </span>
          </button>
        </div>
      </div>
    </div>

    <!--
      Advanced filters modal. Teleported to <body> because FilterBar's root
      uses `backdrop-blur`, which creates a containing block for
      `position: fixed` descendants — without the Teleport the popup gets
      clipped inside the bar instead of overlaying the viewport.
    -->
    <Teleport to="body">
      <PropertiesFiltersPopup
        v-model="isMoreFiltersOpen"
        :filters="filters"
        @input="onMoreFiltersInput"
        @reset="onMoreFiltersReset"
      />
    </Teleport>
    <ExclusiveInfoModal
      v-if="isExclusiveInfoOpen"
      @close="closeExclusiveInfo"
    />
  </div>
</template>

<style scoped lang="scss">
// Dual-handle range slider — two stacked `<input type="range">` overlap on the
// same track. The inputs themselves are transparent (no built-in track shown)
// and disabled to pointer events, EXCEPT for the thumbs which re-enable
// `pointer-events: auto` so each thumb can be grabbed independently. The
// track + colored fill are rendered as separate divs in the template.
.price-slider__input {
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  pointer-events: none;
  height: 100%;
  margin: 0;

  &:focus {
    outline: none;
  }

  // WebKit (Chrome, Safari) thumb
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 18px;
    height: 18px;
    border-radius: 9999px;
    background: #ffffff;
    border: 2px solid #0d0f15;
    box-shadow: 0 1px 3px rgba(13, 15, 21, 0.2);
    cursor: grab;
    pointer-events: auto;
    margin-top: 0;

    &:active {
      cursor: grabbing;
    }
  }

  // Firefox thumb
  &::-moz-range-thumb {
    width: 18px;
    height: 18px;
    border-radius: 9999px;
    background: #ffffff;
    border: 2px solid #0d0f15;
    box-shadow: 0 1px 3px rgba(13, 15, 21, 0.2);
    cursor: grab;
    pointer-events: auto;

    &:active {
      cursor: grabbing;
    }
  }

  // Hide the default WebKit / Firefox tracks so only our custom track shows.
  &::-webkit-slider-runnable-track {
    height: 100%;
    background: transparent;
    border: none;
  }
  &::-moz-range-track {
    height: 100%;
    background: transparent;
    border: none;
  }
}
</style>
